import React, { useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Search, Bell, Settings, User, LogOut, Home, Users,
  MessageSquare, BriefcaseIcon, FileText, Building
} from 'lucide-react';
import { useAuth, useNotifications, useConnections, useSearch } from '../../context';
import { useMessages } from '../../hooks';
import GlobalSearchResults from '../GlobalSearchResults';

/**
 * Main Navigation Component
 * Two-tier navigation: Top bar with search and actions, Bottom bar with main tabs
 * Now uses contexts for state management instead of local state
 */
const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { unreadCount: notificationUnreadCount } = useNotifications();
  const { connectionRequests } = useConnections();
  const { unreadCount: messageUnreadCount, fetchUnreadCount } = useMessages();
  const {
    searchQuery,
    setSearchQuery,
    searchResults,
    showSearchResults,
    setShowSearchResults,
    performSearch,
  } = useSearch();
  const searchRef = useRef(null);

  // Fetch initial message unread count
  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  // Determine current active tab from URL
  const currentPath = location.pathname;
  const mainTab = currentPath.startsWith('/feed') ? 'feed'
    : currentPath.startsWith('/myposts') ? 'myposts'
    : currentPath.startsWith('/people') ? 'people'
    : currentPath.startsWith('/messages') ? 'messages'
    : currentPath.startsWith('/jobs') ? 'jobs'
    : currentPath.startsWith('/companies') ? 'companies'
    : currentPath.startsWith('/profile') ? 'profile'
    : currentPath.startsWith('/in/') ? 'profile'
    : 'feed';

  // Navigate to search results page
  const navigateToSearchResults = () => {
    if (searchQuery.trim()) {
      setShowSearchResults(false);
      navigate(`/search/results/all?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  // Handle tab navigation
  const navigateToTab = (tabId) => {
    const routes = {
      feed: '/feed',
      myposts: '/myposts',
      people: '/people',
      messages: '/messages',
      jobs: '/jobs',
      companies: '/companies',
      profile: user?.userNumber ? `/in/${user.userNumber}` : '/profile',
      notifications: '/notifications',
      settings: '/settings',
    };
    navigate(routes[tabId] || '/feed');
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const mainTabs = [
    { id: 'feed', label: 'Feed', icon: Home },
    { id: 'myposts', label: 'My Posts', icon: FileText },
    { id: 'people', label: 'People', icon: Users, badge: connectionRequests.length },
    { id: 'messages', label: 'Messages', icon: MessageSquare, badge: messageUnreadCount },
    { id: 'jobs', label: 'Jobs', icon: BriefcaseIcon },
    { id: 'companies', label: 'Companies', icon: Building },
  ];

  return (
    <nav className="sticky top-0 z-50">
      {/* Top Tier: Logo, Search, Action Icons */}
      <div className="bg-gray-100 px-6 py-3 border-b border-gray-200">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Logo */}
          <div
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => navigateToTab('feed')}
          >
            <img src="/logo.svg" alt="CosmiWork" className="h-9 w-9" />
            <h1 className="text-xl font-bold text-[#4169E1]">CosmiWork</h1>
          </div>

          {/* Centered Search Bar */}
          <div className="flex-1 max-w-xl mx-8 relative" ref={searchRef}>
            <div className="relative flex gap-2">
              <div className="relative flex-1">
                <Search
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  size={18}
                />
                <input
                  type="text"
                  placeholder="Search everything..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    if (e.target.value.length >= 2) {
                      performSearch(e.target.value);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && searchQuery.trim()) {
                      e.preventDefault();
                      navigateToSearchResults();
                    }
                    if (e.key === 'Escape') {
                      setSearchQuery('');
                      setShowSearchResults(false);
                    }
                  }}
                  onFocus={() => {
                    if (searchResults.length > 0) {
                      setShowSearchResults(true);
                    }
                  }}
                  className="w-full pl-10 pr-4 py-2 bg-white border border-gray-300 rounded-lg focus:border-[#4169E1] focus:outline-none focus:ring-2 focus:ring-[#4169E1] transition"
                />
              </div>
              <button
                onClick={navigateToSearchResults}
                disabled={!searchQuery.trim()}
                className="p-2 bg-[#4169E1] text-white rounded-lg hover:bg-[#3557C5] disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center"
                title="Search"
              >
                <Search size={18} />
              </button>
            </div>

            {/* Search Results Dropdown */}
            {showSearchResults && (
              <GlobalSearchResults
                results={searchResults}
                onConnect={() => {}}
                onFollow={() => {}}
                onViewProfile={(userId) => {
                  setShowSearchResults(false);
                  navigate(`/in/${userId}`);
                }}
                onSelectJob={() => {
                  setShowSearchResults(false);
                  navigateToTab('jobs');
                }}
                onSelectPost={() => {
                  setShowSearchResults(false);
                  navigateToTab('feed');
                }}
                onViewCompany={(companyId) => {
                  setShowSearchResults(false);
                  navigate(`/companies/${companyId}`);
                }}
                onClose={() => {
                  setShowSearchResults(false);
                  setSearchQuery('');
                }}
              />
            )}
          </div>

          {/* Right Action Icons */}
          <div className="flex items-center gap-2">
            {/* Notifications Icon with Badge */}
            <button
              onClick={() => navigateToTab('notifications')}
              className="relative p-2 hover:bg-gray-200 rounded-lg transition"
              title="Notifications"
            >
              <Bell size={20} className="text-gray-700" />
              {notificationUnreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {notificationUnreadCount}
                </span>
              )}
            </button>

            {/* Settings Icon */}
            <button
              onClick={() => navigateToTab('settings')}
              className="p-2 hover:bg-gray-200 rounded-lg transition"
              title="Settings"
            >
              <Settings size={20} className="text-gray-700" />
            </button>

            {/* Profile Icon */}
            <button
              onClick={() => navigateToTab('profile')}
              className="p-2 hover:bg-gray-200 rounded-lg transition"
              title="Profile"
            >
              <User size={20} className="text-gray-700" />
            </button>

            {/* Logout Icon */}
            <button
              onClick={handleLogout}
              className="p-2 hover:bg-red-100 rounded-lg transition text-red-600"
              title="Logout"
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Bottom Tier: Main Navigation Tabs */}
      <div className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-center gap-2 py-3">
            {mainTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => navigateToTab(tab.id)}
                  className={`relative flex items-center gap-2 px-4 py-2 rounded-lg transition font-medium ${
                    mainTab === tab.id
                      ? 'bg-[#4169E1] text-white'
                      : 'text-gray-600 hover:text-[#4169E1] hover:bg-blue-50'
                  }`}
                >
                  <Icon size={20} />
                  <span>{tab.label}</span>
                  {tab.badge > 0 && (
                    <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
